import { useEffect, useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase-config";
import { useNavigate } from "react-router-dom";

export default function SuggestMutualFundFlow() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [data, setData] = useState({
    goal: "",
    horizon: "",
    style: "",
    risk: "",
    hasMF: "",
    name: "",
    mobile: "",
    email: "",
    investment_capacity: "",
    hasEmergencyFund: "",
  });

  const next = () => {
    setErrors({});
    setStep((s) => s + 1);
  };

  const back = () => {
    setErrors({});
    setStep((s) => s - 1);
  };

  /* ================= VALIDATION HELPERS ================= */
  const isValidMobile = (mobile) => /^[6-9]\d{9}$/.test(mobile);
  const isValidEmail = (email) => /^\S+@\S+\.\S+$/.test(email);

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    const newErrors = {};

    if (!data.name || data.name.trim().length < 2) {
      newErrors.name = "Please enter your full name";
    }

    if (!isValidMobile(data.mobile)) {
      newErrors.mobile = "Enter a valid 10-digit mobile number";
    }

    if (data.email && !isValidEmail(data.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await addDoc(collection(db, "mf_guidance_requests"), {
        ...data,
        source: "mutual-fund-check",
        status: "new",
        createdAt: serverTimestamp(),
      });

      setStep(9); // success
    } catch (err) {
      console.error("Error submitting mutual fund guidance request:", err);
      setErrors({ submit: "Something went wrong. Please try again." });
    }

    setLoading(false);
  };

  useEffect(() => {
    if (step === 9) {
      const timer = setTimeout(() => {
        navigate("/mutual-funds", { replace: true });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [step]);

  return (
    <section className="bg-black-main-dark text-gray-200">
      <div className="flex justify-center items-center min-h-screen px-4">
        <div className="max-w-md w-full bg-black-main-dark border border-gray-800 rounded-3xl p-8 text-white">
          {/* PROGRESS */}
          {step <= 6 && (
            <div className="mb-6">
              <div className="h-1 bg-gray-800 rounded">
                <div
                  className="h-1 bg-primary1 rounded transition-all"
                  style={{ width: `${(step - 1) * 16.6}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">Step {step} of 8</p>
            </div>
          )}

          {/* STEP 1 */}
          {step === 1 && (
            <>
              <h3 className="text-xl font-semibold mb-4">
                What is your primary goal?
              </h3>
              {[
                "Wealth creation",
                "Child education",
                "Retirement",
                "Tax saving",
                "Other goal",
              ].map((g) => (
                <button
                  key={g}
                  onClick={() => {
                    setData({ ...data, goal: g });
                    next();
                  }}
                  className="w-full py-3 mb-3 border border-gray-700 rounded-xl hover:border-primary1"
                >
                  {g}
                </button>
              ))}
            </>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <>
              <h3 className="text-xl font-semibold mb-4">
                Investment time horizon?
              </h3>
              {["Less than 3 years", "3–7 years", "More than 7 years"].map(
                (h) => (
                  <button
                    key={h}
                    onClick={() => {
                      setData({ ...data, horizon: h });
                      next();
                    }}
                    className="w-full py-3 mb-3 border border-gray-700 rounded-xl hover:border-primary1"
                  >
                    {h}
                  </button>
                ),
              )}
              <button onClick={back} className="text-xs text-gray-500 mt-2">
                ← Back
              </button>
            </>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <>
              <h3 className="text-xl font-semibold mb-4">
                How much are you planning to invest?
              </h3>
              {[
                "Below ₹5,000 per month",
                "₹5,000 – ₹15,000 per month",
                "More than ₹15,000 per month",
                "Not decided yet",
              ].map((h) => (
                <button
                  key={h}
                  onClick={() => {
                    setData({ ...data, investment_capacity: h });
                    next();
                  }}
                  className="w-full py-3 mb-3 border border-gray-700 rounded-xl hover:border-primary1"
                >
                  {h}
                </button>
              ))}
              <button onClick={back} className="text-xs text-gray-500 mt-2">
                ← Back
              </button>
            </>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <>
              <h3 className="text-xl font-semibold mb-4">
                How do you want to invest?
              </h3>
              {["Monthly SIP", "One-time Lumpsum", "Both"].map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setData({ ...data, style: s });
                    next();
                  }}
                  className="w-full py-3 mb-3 border border-gray-700 rounded-xl hover:border-primary1"
                >
                  {s}
                </button>
              ))}
              <button onClick={back} className="text-xs text-gray-500 mt-2">
                ← Back
              </button>
            </>
          )}

          {/* STEP 5 */}
          {step === 5 && (
            <>
              <h3 className="text-xl font-semibold mb-4">
                If your investment falls 20% in a market correction, what would
                you do?
              </h3>
              {[
                "I would exit immediately",
                "I would wait patiently",
                "I would invest more",
                "I’m not sure",
              ].map((r) => (
                <button
                  key={r}
                  onClick={() => {
                    setData({ ...data, risk: r });
                    next();
                  }}
                  className="w-full py-3 mb-3 border border-gray-700 rounded-xl hover:border-primary1"
                >
                  {r}
                </button>
              ))}
              <button onClick={back} className="text-xs text-gray-500 mt-2">
                ← Back
              </button>
            </>
          )}

          {/* STEP 6 */}
          {step === 6 && (
            <>
              <h3 className="text-xl font-semibold mb-4">
                Are you currently investing in mutual funds?
              </h3>
              {[
                "Yes, regularly",
                "Yes, but irregularly",
                "No, just starting",
              ].map((ans) => (
                <button
                  key={ans}
                  onClick={() => {
                    setData({ ...data, hasMF: ans });
                    next();
                  }}
                  className="w-full py-3 mb-3 border border-gray-700 rounded-xl hover:border-primary1"
                >
                  {ans}
                </button>
              ))}
              <button onClick={back} className="text-xs text-gray-500 mt-2">
                ← Back
              </button>
            </>
          )}

          {/* STEP 7 */}
          {step === 7 && (
            <>
              <h3 className="text-xl font-semibold mb-4">
                Do you currently have an emergency fund (6 months of expenses)?
              </h3>
              {["Yes", "Partially", "No"].map((ans) => (
                <button
                  key={ans}
                  onClick={() => {
                    setData({ ...data, hasEmergencyFund: ans });
                    next();
                  }}
                  className="w-full py-3 mb-3 border border-gray-700 rounded-xl hover:border-primary1"
                >
                  {ans}
                </button>
              ))}
              <button onClick={back} className="text-xs text-gray-500 mt-2">
                ← Back
              </button>
            </>
          )}

          {/* STEP 8 */}
          {step === 8 && (
            <>
              <h3 className="text-xl font-semibold mb-4">
                Where should we share guidance?
              </h3>

              <input
                placeholder="Your Name"
                className={`w-full mb-2 bg-black border rounded-xl px-4 py-3 ${
                  errors.name ? "border-red-500" : "border-gray-700"
                }`}
                value={data.name}
                onChange={(e) => setData({ ...data, name: e.target.value })}
              />

              {errors.name && (
                <p className="text-xs text-red-400 mb-2">{errors.name}</p>
              )}

              <input
                placeholder="Mobile Number"
                className={`w-full mb-2 bg-black border rounded-xl px-4 py-3 ${
                  errors.mobile ? "border-red-500" : "border-gray-700"
                }`}
                value={data.mobile}
                onChange={(e) => setData({ ...data, mobile: e.target.value })}
              />

              {errors.mobile && (
                <p className="text-xs text-red-400 mb-2">{errors.mobile}</p>
              )}

              <input
                placeholder="Email (optional)"
                className={`w-full mb-2 bg-black border rounded-xl px-4 py-3 ${
                  errors.email ? "border-red-500" : "border-gray-700"
                }`}
                value={data.email}
                onChange={(e) => setData({ ...data, email: e.target.value })}
              />

              {errors.email && (
                <p className="text-xs text-red-400 mb-2">{errors.email}</p>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-primary1 text-black py-3 rounded-xl font-medium mt-2"
              >
                {loading ? "Submitting..." : "Get Mutual Fund Guidance"}
              </button>

              {errors.submit && (
                <p className="text-xs text-red-400 text-center mt-3">
                  {errors.submit}
                </p>
              )}

              <p className="text-xs text-gray-500 text-center mt-4">
                No tips. No pressure. Based on your goals & risk comfort.
              </p>
            </>
          )}

          {/* SUCCESS */}
          {step === 9 && (
            <div className="text-center">
              <h1 className="text-2xl font-semibold text-white mb-2">
                Thank you!!
              </h1>
              <p className="text-gray-400 text-md">
                We’ll review your inputs and contact you shortly.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Redirecting to home...
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
